import React, { useState, useEffect, useCallback } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useCompany } from "../../../contexts/CompanyContext";
import { useColorMode } from "../../../contexts/ColorModeContext";
import { getAdminChartColors } from "./chartTheme";
import { adminApi } from "../../../services";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

interface MonthlyRevenueData {
  month: string;
  revenue: number;
}

interface Next12MonthsRevenueChartProps {
  professionalId?: number;
}

export const Next12MonthsRevenueChart: React.FC<Next12MonthsRevenueChartProps> = ({
  professionalId,
}) => {
  const { company } = useCompany();
  const [data, setData] = useState<MonthlyRevenueData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMonthlyRevenue = useCallback(async () => {
    if (!company?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Only pass professionalId if company is not car
      const professionalIdToUse = company?.category !== "car" ? professionalId : undefined;
      const response = await adminApi.getNext12MonthsRevenue(professionalIdToUse);

      if (response.success && response.data) {
        setData(response.data);
      } else {
        throw new Error(response.error || "Failed to fetch monthly revenue");
      }
    } catch (err) {
      setError("Erro ao carregar receita mensal");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [company?.id, company?.category, professionalId]);

  useEffect(() => {
    fetchMonthlyRevenue();
  }, [fetchMonthlyRevenue]);

  const MonthlyRevenueChartContent: React.FC = () => {
    const { isDark } = useColorMode();
    const chartColors = getAdminChartColors(isDark);
    const chartConfig = {
      data: {
        labels: (data || []).map((item) => item.month),
        datasets: [
          {
            label: "Receita Mensal (R$)",
            data: (data || []).map((item) => item.revenue),
            backgroundColor: "rgba(59, 130, 246, 0.8)",
            borderColor: "rgba(59, 130, 246, 1)",
            borderWidth: 1,
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: "bottom" as const,
            labels: { color: chartColors.text },
          },
          title: {
            display: true,
            text: "Receita dos Próximos 12 Meses",
            color: chartColors.title,
            padding: {
              bottom: 20,
            },
            font: {
              size: window.innerWidth < 640 ? 14 : 16,
            },
          },
          datalabels: {
            display: window.innerWidth >= 640,
            anchor: "end" as const,
            align: "top" as const,
            formatter: function (value: any) {
              return `R$ ${value.toLocaleString()}`;
            },
            color: chartColors.text,
            font: {
              weight: "bold" as const,
              size: window.innerWidth < 640 ? 9 : 11,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: chartColors.grid },
            ticks: {
              callback: function (value: any) {
                return `R$ ${value.toLocaleString()}`;
              },
              color: chartColors.muted,
              font: {
                size: window.innerWidth < 640 ? 10 : 12,
              },
            },
          },
          x: {
            grid: { color: chartColors.grid },
            ticks: {
              color: chartColors.muted,
              font: {
                size: window.innerWidth < 640 ? 10 : 12,
              },
            },
          },
        },
      },
    };

    return <Bar data={chartConfig.data} options={chartConfig.options} />;
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-3 sm:p-4 md:p-6">
      {loading ? (
        <div className="flex items-center justify-center py-12 sm:py-16 md:py-20">
          <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg p-3 sm:p-4">
          <p className="text-red-600 dark:text-red-400 text-xs sm:text-sm">{error}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <MonthlyRevenueChartContent />
        </div>
      )}
    </div>
  );
};
